/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button/Button";
import { Typography } from "@/components/ui/Typography/Typography";
import { Input } from "@/components/forms/Input/Input";
import { Switch } from "@/components/ui/Switch";
import {
  GET_ROLE_LIST_QUERY_KEY,
  useCreateRoleMutation,
  useEnhancedRoleList,
  useRoleHierarchyPreviewMutation,
  useUpdateRoleMutation,
} from "@/api/role-management/role-management.hooks";
import {
  TRole,
  TCreateRoleForm,
  TUpdateRoleForm,
  TEnhancedRole,
  TRoleHierarchyPreview,
  THierarchyPlacementMode,
} from "@/api/role-management/role-management.types";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { TApiServiceError } from "@/api/apiService";
import { useToast } from "@/components/ui";
import { sanitizeInput } from "@/utils/commonUtils";
import { Spinner, Textarea } from "flowbite-react";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useDebounce } from "@/hooks/useDebounce";
import {
  RoleHierarchyPlacementFields,
  RoleHierarchyPreviewPanel,
  TPlacementFormValues,
} from "./RoleHierarchyPlacement";
import { RoleHierarchyImpactModal } from "./RoleHierarchyImpactModal";

type TRoleFormProps = {
  role?: TRole | TEnhancedRole;
  onSave: () => void;
  onCancel: () => void;
  isEdit: boolean;
};

const defaultPlacement = (): TPlacementFormValues => ({
  placement_mode: "",
  reference_role_id: "",
  secondary_reference_role_id: "",
});

const PREVIEW_DEBOUNCE_MS = 400;
const ROLE_NAME_PREVIEW_DEBOUNCE_MS = 500;

const roleListFilters = {
  page: 1,
  page_size: 100,
  is_active: true,
  sort_field: "rank",
  sort_order: "asc",
} as const;

export const RoleForm: React.FC<TRoleFormProps> = ({
  role,
  onSave,
  onCancel,
  isEdit,
}) => {
  const { t } = useTranslation(["roleManagementPage"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const previewMutation = useRoleHierarchyPreviewMutation();
  const previewMutateAsyncRef = React.useRef(previewMutation.mutateAsync);
  previewMutateAsyncRef.current = previewMutation.mutateAsync;
  const previewRequestIdRef = React.useRef(0);
  const [placement, setPlacement] =
    React.useState<TPlacementFormValues>(defaultPlacement());
  const [preview, setPreview] = React.useState<TRoleHierarchyPreview | null>(
    null,
  );
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [loadedPreviewKey, setLoadedPreviewKey] = React.useState("");
  const [impactOpen, setImpactOpen] = React.useState(false);
  const [isConfirmingImpact, setIsConfirmingImpact] = React.useState(false);
  const [pendingPayload, setPendingPayload] = React.useState<
    TCreateRoleForm | TUpdateRoleForm | null
  >(null);

  const {
    data: roleListData,
    isLoading: isRolesLoading,
    isFetching: isRolesFetching,
  } = useEnhancedRoleList(roleListFilters);
  const referenceRoles = roleListData?.results ?? [];
  const isReferenceRolesLoading = isRolesLoading || isRolesFetching;

  const schema = React.useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(2, t("ROLES.VALIDATION.ROLE_NAME_MIN_LENGTH"))
          .max(50, t("ROLES.VALIDATION.ROLE_NAME_MAX_LENGTH"))
          .nonempty(t("ROLES.VALIDATION.ROLE_NAME")),
        slug: isEdit
          ? z.string().optional()
          : z
              .string()
              .regex(/^[a-z0-9-]+$/, t("ROLES.VALIDATION.ROLE_SLUG_PATTERN"))
              .min(2, t("ROLES.VALIDATION.ROLE_SLUG_MIN_LENGTH"))
              .max(50, t("ROLES.VALIDATION.ROLE_SLUG_MAX_LENGTH"))
              .nonempty(t("ROLES.VALIDATION.ROLE_SLUG")),
        description: z
          .string()
          .min(10, t("ROLES.VALIDATION.ROLE_DESCRIPTION_MIN_LENGTH"))
          .max(500, t("ROLES.VALIDATION.ROLE_DESCRIPTION_MAX_LENGTH"))
          .nonempty(t("ROLES.VALIDATION.ROLE_DESCRIPTION")),
        is_active: z.boolean(),
      }),
    [t, isEdit],
  );

  const [placementFieldErrors, setPlacementFieldErrors] = React.useState<
    Record<string, string>
  >({});

  const createRoleMutation = useCreateRoleMutation({
    onSuccess: () => undefined,
    onError: () => undefined,
  });
  const updateRoleMutation = useUpdateRoleMutation({
    onSuccess: () => undefined,
    onError: () => undefined,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      name: role?.name ?? "",
      slug: role?.slug ?? "",
      description: role?.description ?? "",
      is_active: role?.is_active ?? true,
    },
  });

  const nameField = register("name");
  const slugField = register("slug");
  const descriptionField = register("description");

  const validatePlacementFields = useCallback((): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};
    if (!placement.placement_mode) {
      fieldErrors.placement_mode = t("ROLES.HIERARCHY.POSITION_REQUIRED");
    }
    if (!placement.reference_role_id) {
      fieldErrors.reference_role_id = t("ROLES.HIERARCHY.REFERENCE_REQUIRED");
    }
    if (
      placement.placement_mode === "between" &&
      !placement.secondary_reference_role_id
    ) {
      fieldErrors.secondary_reference_role_id = t(
        "ROLES.HIERARCHY.SECONDARY_REFERENCE_REQUIRED",
      );
    }
    return fieldErrors;
  }, [placement, t]);

  const watchName = watch("name");
  const debouncedRoleName = useDebounce(
    watchName,
    ROLE_NAME_PREVIEW_DEBOUNCE_MS,
  );
  const watchIsActive = watch("is_active");

  React.useEffect(() => {
    if (!isEdit && watchName) {
      const slug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setValue("slug", slug);
    }
  }, [watchName, isEdit, setValue]);

  const buildHierarchyPlacement = useCallback(() => {
    if (!placement.placement_mode || !placement.reference_role_id) {
      return undefined;
    }
    return {
      mode: placement.placement_mode as THierarchyPlacementMode,
      reference_role_id: placement.reference_role_id,
      ...(placement.placement_mode === "between"
        ? { secondary_reference_role_id: placement.secondary_reference_role_id }
        : {}),
    };
  }, [
    placement.placement_mode,
    placement.reference_role_id,
    placement.secondary_reference_role_id,
  ]);

  const isSaving = createRoleMutation.isPending || updateRoleMutation.isPending;

  const placementPreviewKey = React.useMemo(() => {
    if (!placement.placement_mode || !placement.reference_role_id) {
      return "";
    }
    if (
      placement.placement_mode === "between" &&
      !placement.secondary_reference_role_id
    ) {
      return "";
    }
    return JSON.stringify({
      mode: placement.placement_mode,
      reference_role_id: placement.reference_role_id,
      secondary_reference_role_id: placement.secondary_reference_role_id ?? "",
      role_internal_id: isEdit ? role?.internal_id : "",
      new_role_name: debouncedRoleName.trim(),
    });
  }, [
    placement.placement_mode,
    placement.reference_role_id,
    placement.secondary_reference_role_id,
    isEdit,
    role?.internal_id,
    debouncedRoleName,
  ]);

  React.useEffect(() => {
    if (isSaving) {
      return;
    }

    if (!placementPreviewKey) {
      previewRequestIdRef.current += 1;
      setPreview(null);
      setLoadedPreviewKey("");
      setIsPreviewLoading(false);
      return;
    }

    setIsPreviewLoading(true);

    const timer = window.setTimeout(() => {
      const hierarchy_placement = buildHierarchyPlacement();
      if (!hierarchy_placement?.reference_role_id) {
        return;
      }

      const requestId = ++previewRequestIdRef.current;
      const keyAtRequest = placementPreviewKey;

      void previewMutateAsyncRef
        .current({
          hierarchy_placement,
          role_internal_id: isEdit ? role?.internal_id : undefined,
          new_role_name: debouncedRoleName.trim() || undefined,
        })
        .then((data) => {
          if (requestId !== previewRequestIdRef.current) {
            return;
          }
          setPreview(data);
          setLoadedPreviewKey(keyAtRequest);
        })
        .catch(() => {
          if (requestId !== previewRequestIdRef.current) {
            return;
          }
          setPreview(null);
          setLoadedPreviewKey("");
        })
        .finally(() => {
          if (requestId === previewRequestIdRef.current) {
            setIsPreviewLoading(false);
          }
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    placementPreviewKey,
    isSaving,
    buildHierarchyPlacement,
    isEdit,
    role?.internal_id,
    debouncedRoleName,
  ]);

  const getHierarchyConfirmationErrors = (
    error: TApiServiceError,
  ):
    | {
        requires_confirmation?: boolean;
        impact?: TRoleHierarchyPreview;
      }
    | undefined => {
    const body = error.response?.data as
      | {
          data?: {
            errors?: {
              requires_confirmation?: boolean;
              impact?: TRoleHierarchyPreview;
            };
          };
          errors?: {
            requires_confirmation?: boolean;
            impact?: TRoleHierarchyPreview;
          };
        }
      | undefined;
    return body?.data?.errors ?? body?.errors;
  };

  const submitPayload = async (
    payload: TCreateRoleForm | TUpdateRoleForm,
    confirm = false,
  ) => {
    const withConfirm = { ...payload, confirm_hierarchy_impact: confirm };
    try {
      if (isEdit) {
        await updateRoleMutation.mutateAsync(withConfirm as TUpdateRoleForm);
      } else {
        await createRoleMutation.mutateAsync(withConfirm as TCreateRoleForm);
      }
      void queryClient.invalidateQueries({
        queryKey: [GET_ROLE_LIST_QUERY_KEY],
      });
      toast({
        title: isEdit
          ? t("ROLES.MESSAGES.UPDATE_SUCCESS")
          : t("ROLES.MESSAGES.CREATE_SUCCESS"),
        variant: "green",
      });
      setImpactOpen(false);
      onSave();
    } catch (error) {
      const errorsPayload = getHierarchyConfirmationErrors(
        error as TApiServiceError,
      );
      if (errorsPayload?.requires_confirmation) {
        setPendingPayload(payload);
        setPreview(errorsPayload.impact ?? preview);
        setImpactOpen(true);
        return;
      }
      throw error;
    }
  };

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (
      (isEdit && !hasPermission("can-update-role-group")) ||
      (!isEdit && !hasPermission("can-create-role-group"))
    ) {
      return;
    }

    const hierarchyFieldErrors = validatePlacementFields();
    if (Object.keys(hierarchyFieldErrors).length > 0) {
      setPlacementFieldErrors(hierarchyFieldErrors);
      return;
    }
    setPlacementFieldErrors({});

    const hierarchy_placement = buildHierarchyPlacement();

    const payload: TCreateRoleForm | TUpdateRoleForm = isEdit
      ? {
          internal_id: role!.internal_id,
          ...data,
          slug: role!.slug,
          hierarchy_placement,
        }
      : {
          ...data,
          slug: data.slug ?? "",
          hierarchy_placement,
        };

    if (preview?.requires_confirmation) {
      setPendingPayload(payload);
      setImpactOpen(true);
      return;
    }

    try {
      await submitPayload(payload, false);
    } catch (error) {
      const apiError = error as TApiServiceError;
      toast({
        title: apiError.message ?? t("ROLES.MESSAGES.SUBMIT_FAILED"),
        variant: "destructive",
      });
    }
  };

  const isLoading = isSaving;

  const isHierarchyPreviewPending = Boolean(
    placementPreviewKey &&
    (isPreviewLoading || loadedPreviewKey !== placementPreviewKey || !preview),
  );

  const isSubmitDisabled = isSaving || isHierarchyPreviewPending;

  const handleInvalidSubmit = useCallback(() => {
    setPlacementFieldErrors(validatePlacementFields());
  }, [validatePlacementFields]);

  const handlePlacementChange = useCallback(
    (values: Partial<TPlacementFormValues>) => {
      setPlacement((current) => ({ ...current, ...values }));
      setPlacementFieldErrors((current) => {
        if (Object.keys(current).length === 0) {
          return current;
        }
        const next = { ...current };
        for (const key of Object.keys(
          values,
        ) as (keyof TPlacementFormValues)[]) {
          delete next[key];
        }
        return next;
      });
    },
    [],
  );

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium ">
              {t("ROLES.CREATE_ROLE.ROLE_NAME")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <Input
              id="name"
              type="text"
              placeholder={t("ROLES.CREATE_ROLE.ROLE_NAME_PLACEHOLDER")}
              name={nameField.name}
              ref={nameField.ref}
              onBlur={nameField.onBlur}
              className={errors.name ? "border-red-500" : ""}
              onChange={(e) =>
                void setValue("name", sanitizeInput(e.target.value), {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
            {errors.name && (
              <Typography variant="small" className="text-red-600">
                {errors.name.message}
              </Typography>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="slug" className="block text-sm font-medium ">
              {t("ROLES.CREATE_ROLE.ROLE_SLUG")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <Input
              id="slug"
              type="text"
              placeholder={t("ROLES.CREATE_ROLE.ROLE_SLUG_PLACEHOLDER")}
              name={slugField.name}
              ref={slugField.ref}
              onBlur={slugField.onBlur}
              className={errors.slug ? "border-red-500" : ""}
              onChange={(e) =>
                void setValue("slug", sanitizeInput(e.target.value), {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              readOnly={isEdit}
              disabled={isEdit}
            />
            {errors.slug && (
              <Typography variant="small" className="text-red-600">
                {errors.slug.message}
              </Typography>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 dark:text-muted-foreground"
          >
            {t("ROLES.CREATE_ROLE.ROLE_DESCRIPTION")}
            <span className="text-red-600">*</span>
          </label>
          <Textarea
            id="description"
            rows={3}
            placeholder={t("ROLES.CREATE_ROLE.ROLE_DESCRIPTION_PLACEHOLDER")}
            name={descriptionField.name}
            ref={descriptionField.ref}
            onBlur={descriptionField.onBlur}
            onChange={(e) =>
              void setValue("description", sanitizeInput(e.target.value), {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.description
                ? "border-red-500"
                : "border-gray-300 dark:border-gray-700"
            }`}
          />
          {errors.description && (
            <Typography variant="small" className="text-red-600">
              {errors.description.message}
            </Typography>
          )}
        </div>

        <RoleHierarchyPlacementFields
          roles={referenceRoles}
          rolesLoading={isReferenceRolesLoading}
          values={placement}
          errors={placementFieldErrors}
          excludeRoleId={isEdit ? role?.internal_id : undefined}
          onChange={handlePlacementChange}
          activeToggle={
            <>
              <label
                htmlFor="is_active"
                className="block text-sm font-medium text-gray-700 dark:text-muted-foreground"
              >
                {t("ROLES.CREATE_ROLE.ACTIVE_ROLE_TOGGLE")}
              </label>
              <div className="flex h-10 items-center">
                <Switch
                  checked={watchIsActive}
                  onCheckedChange={(checked: boolean) =>
                    setValue("is_active", checked)
                  }
                  id="is_active"
                />
              </div>
            </>
          }
        />

        {!isSaving && (
          <RoleHierarchyPreviewPanel
            preview={preview}
            isLoading={isPreviewLoading}
          />
        )}

        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("GLOBAL.CANCEL")}
          </Button>
          <PermissionGuard
            permissions={[
              isEdit ? "can-update-role-group" : "can-create-role-group",
            ]}
          >
            <Button type="submit" disabled={isSubmitDisabled} size="sm">
              {isEdit
                ? t("ROLES.BUTTONS.UPDATE_ROLE")
                : t("ROLES.BUTTONS.CREATE_ROLE")}
              {isLoading && <Spinner size="sm" className="ms-1" />}
            </Button>
          </PermissionGuard>
        </div>
      </form>

      <RoleHierarchyImpactModal
        open={impactOpen}
        preview={preview}
        onCancel={() => {
          if (isConfirmingImpact || isSaving) {
            return;
          }
          setImpactOpen(false);
        }}
        onConfirm={() => {
          if (!pendingPayload || isConfirmingImpact || isSaving) {
            return;
          }
          setIsConfirmingImpact(true);
          void submitPayload(pendingPayload, true)
            .catch((error) => {
              const apiError = error as TApiServiceError;
              toast({
                title: apiError.message ?? t("ROLES.MESSAGES.SUBMIT_FAILED"),
                variant: "destructive",
              });
            })
            .finally(() => {
              setIsConfirmingImpact(false);
            });
        }}
        isLoading={impactOpen && (isSaving || isConfirmingImpact)}
      />
    </>
  );
};
